import {
  ArchiveBoxIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  PencilSquareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import debounce from 'debounce-promise';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactSelect, { type MultiValue, type SingleValue } from 'react-select';
import ReactAsyncSelect from 'react-select/async';
import { Button, FormActions, FormDialog } from '~/components';
import { FormSelectInput, selectPortalProps, SelectStyles } from '~/components/forms/Select/Select';
import type { AvailabilityDay, AvailabilityRule } from '~/features/provider-hlx/calendar/types';
import { type AreaOfInterestApi, loadAreasOfInterest } from './aoiApi';
import { AvailabilitySummarySection } from './AvailabilitySummarySection';
import { MIN_LOCATION_SEARCH_CHARS, searchGeographies } from './geographyApi';
import type {
  ExpectedRateErrors,
  FormState,
  GeographyOption,
  SelectedSubspecialty,
} from './providerServiceListingForm';
import {
  formatKind,
  formatWorkLocation,
  LISTING_KIND_OPTIONS,
  parseNonnegativeNumber,
  pickedSpecialties,
  SHIFT_LABEL_OPTIONS,
  toggleValue,
  withoutOrphanedSubspecialties,
  WORK_LOCATION_OPTIONS,
} from './providerServiceListingForm';
import type { AvailabilityMode, CompType, PublicationFeedback, ServiceListing } from './types';

const FieldLabel = ({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) => (
  <label
    className="block text-left text-xs font-medium uppercase tracking-wide text-content-tertiary"
    htmlFor={htmlFor}
  >
    {children}
  </label>
);

type LocationOption = { label: string; selection: GeographyOption | null; value: string };

const toLocationOption = (selection: GeographyOption): LocationOption => ({
  label: selection.label,
  selection,
  value: String(selection.id),
});

// One search per pause in typing rather than per keystroke; a failed lookup just shows no matches.
const loadLocationOptions = debounce(async (input: string): Promise<LocationOption[]> => {
  if (input.trim().length < MIN_LOCATION_SEARCH_CHARS) return [];
  try {
    return (await searchGeographies(input)).map(toLocationOption);
  } catch {
    return [];
  }
}, 300);

// New locations come from the reference list, so the coordinates and ZIP behind a listing are real.
// A location saved before this picker existed has no reference row; it is shown as the text it was
// until someone picks a replacement.
const LocationSelect = ({
  formState,
  setFormState,
}: {
  formState: FormState;
  setFormState: Dispatch<SetStateAction<FormState>>;
}) => {
  const savedText = formState.geographyQuery.trim();
  const value: LocationOption | null = formState.geographySelection
    ? toLocationOption(formState.geographySelection)
    : savedText
      ? { label: savedText, selection: null, value: 'saved-location' }
      : null;

  return (
    <div data-cy="provider-service-location">
      <ReactAsyncSelect<LocationOption, false>
        cacheOptions
        classNames={SelectStyles()}
        components={{ Input: FormSelectInput }}
        inputId="geography-query"
        isClearable
        loadOptions={loadLocationOptions}
        loadingMessage={() => 'Searching...'}
        menuPortalTarget={selectPortalProps.menuPortalTarget}
        menuPosition={selectPortalProps.menuPosition}
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim().length < MIN_LOCATION_SEARCH_CHARS
            ? 'Type a city or ZIP code'
            : 'No matching locations'
        }
        onChange={(option: SingleValue<LocationOption>) =>
          setFormState((current) => ({
            ...current,
            geographyQuery: option?.label ?? '',
            geographySelection: option?.selection ?? null,
          }))
        }
        placeholder="City, ST or ZIP"
        styles={{ menuPortal: selectPortalProps.menuPortalStyle }}
        unstyled
        value={value}
      />
    </div>
  );
};

const TextInput = ({
  error,
  id,
  min,
  onChange,
  placeholder,
  step,
  type = 'text',
  value,
}: {
  error?: string;
  id: string;
  min?: number;
  onChange: (value: string) => void;
  placeholder?: string;
  step?: number;
  type?: string;
  value: string;
}) => {
  const errorId = `${id}-error`;
  // [MCV-1547] Mirrors the shared TextInput, which this locally controlled editor cannot use:
  // no numeric field here takes a negative figure, so a number input refuses one by default.
  // `min` stops the spinner, the keydown stops typing, and the fold in onChange stops paste and
  // autofill. A value is folded by its numeric value, never by stripping '-': in "1e-3" the
  // minus is part of a positive exponent, and deleting it turns 0.001 into 1000.
  const blockNegative = type === 'number';
  const resolvedMin = blockNegative && min === undefined ? 0 : min;
  // `min` also switches on the browser's step-mismatch check, whose default step of 1 would make
  // a radius of 2.5 natively invalid inside this native <form> (no message, no submit).
  const resolvedStep = step ?? (blockNegative ? 'any' : undefined);
  return (
    <>
      <input
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
        className={`mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm text-content-primary outline-none transition focus:ring-3 ${
          error
            ? 'border-danger focus:border-danger focus:ring-danger/12'
            : 'border-grey-800 focus:border-primary-50 focus:ring-primary-50/12'
        }`}
        id={id}
        min={resolvedMin}
        onChange={(event) => {
          const { value: next } = event.target;
          onChange(
            blockNegative && next !== '' && Number(next) < 0 ? String(Math.abs(Number(next))) : next
          );
        }}
        onKeyDown={
          blockNegative
            ? (event) => {
                if (event.key === '-') event.preventDefault();
              }
            : undefined
        }
        placeholder={placeholder}
        step={resolvedStep}
        type={type}
        value={value}
      />
      {error ? (
        <p className="mt-1 text-left text-xs text-danger" id={errorId}>
          {error}
        </p>
      ) : null}
    </>
  );
};

const MultiSelectPills = <T extends string>({
  isOptionDisabled,
  options,
  selected,
  onToggle,
}: {
  /** A pill that cannot be turned on; one that is already on can still be turned off. */
  isOptionDisabled?: (value: T) => boolean;
  onToggle: (value: T) => void;
  options: Array<{ label: string; value: T }> | T[];
  selected: T[];
}) => (
  <div className="mt-2 flex flex-wrap gap-2">
    {options.map((option) => {
      const value = typeof option === 'string' ? option : option.value;
      const label = typeof option === 'string' ? option : option.label;
      const isSelected = selected.includes(value);
      const isDisabled = !isSelected && Boolean(isOptionDisabled?.(value));

      return (
        <button
          aria-pressed={isSelected}
          className={`rounded-md border px-3 py-2 text-sm font-medium transition ${
            isSelected
              ? 'border-primary-50 bg-primary-50 text-white'
              : 'border-grey-800 bg-white text-content-secondary hover:bg-grey-925'
          } ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
          disabled={isDisabled}
          key={value}
          onClick={() => onToggle(value)}
          type="button"
        >
          {label}
        </button>
      );
    })}
  </div>
);

type AoiSpecialtyOption = {
  label: string;
  value: string;
};

/**
 * The service-listing editor is locally controlled rather than react-hook-form
 * controlled. Keep this select local for that reason: it updates the existing
 * form state directly, while still sharing the visual and accessibility
 * treatment used by the app's react-select form controls.
 */
const AoiSpecialtiesSelect = ({
  onChange,
  options,
  selected,
}: {
  onChange: (values: string[]) => void;
  options: string[];
  selected: string[];
}) => {
  const selectOptions: AoiSpecialtyOption[] = options.map((specialty) => ({
    label: specialty,
    value: specialty,
  }));
  const selectedOptions = selected.map((specialty) => ({ label: specialty, value: specialty }));

  return (
    <div className="mt-2" data-cy="provider-service-aoi-specialties">
      <ReactSelect<AoiSpecialtyOption, true>
        aria-label="AOI Specialties"
        closeMenuOnSelect={false}
        components={{ Input: FormSelectInput }}
        inputId="service-listing-aoi-specialties"
        isClearable
        isMulti
        menuPortalTarget={selectPortalProps.menuPortalTarget}
        menuPosition={selectPortalProps.menuPosition}
        noOptionsMessage={() => 'No canonical specialties available'}
        onChange={(next: MultiValue<AoiSpecialtyOption>) =>
          onChange(next.map((specialty) => specialty.value))
        }
        options={selectOptions}
        placeholder="Select specialties..."
        styles={{
          menuPortal: selectPortalProps.menuPortalStyle,
        }}
        unstyled
        value={selectedOptions}
        classNames={{
          ...SelectStyles(),
          multiValue: () => 'mx-1 rounded-md border border-primary-700 bg-white px-2',
          multiValueLabel: () => 'px-1 py-1 body-text',
          multiValueRemove: () =>
            'cursor-pointer rounded-r-md px-1 hover:bg-grey-900 hover:text-error-50',
        }}
      />
    </div>
  );
};

type SubspecialtyOption = {
  label: string;
  subspecialty: SelectedSubspecialty;
  value: string;
};

/**
 * [MCV-1855] The subspecialties (areas of interest) of every selected specialty. Locally
 * controlled for the same reason as the specialties box. With one specialty an option reads
 * "Aortic Surgery"; with several it names its specialty: "Aortic Surgery (Cardiology)".
 */
const SubspecialtiesSelect = ({
  formState,
  setFormState,
}: {
  formState: FormState;
  setFormState: Dispatch<SetStateAction<FormState>>;
}) => {
  const specialties = pickedSpecialties(formState);
  const specialtyIdsKey = specialties.map((specialty) => specialty.id).join(',');
  const [areasBySpecialty, setAreasBySpecialty] = useState<Record<number, AreaOfInterestApi[]>>({});
  // Specialty ids whose lookup is currently failed; a later success (or removal) clears one.
  const [failedIds, setFailedIds] = useState<number[]>([]);
  const requested = useRef(new Set<number>());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // One fetch per specialty id, remembered while the editor is open.
  useEffect(() => {
    const ids = specialtyIdsKey ? specialtyIdsKey.split(',').map(Number) : [];
    setFailedIds((current) => {
      const stillSelected = current.filter((id) => ids.includes(id));
      return stillSelected.length === current.length ? current : stillSelected;
    });
    ids
      .filter((id) => !requested.current.has(id))
      .forEach((id) => {
        requested.current.add(id);
        loadAreasOfInterest(id)
          .then((areas) => {
            if (!mounted.current) return;
            setAreasBySpecialty((current) => ({ ...current, [id]: areas }));
            setFailedIds((current) => current.filter((failedId) => failedId !== id));
          })
          .catch(() => {
            // Let the next specialty change try again.
            requested.current.delete(id);
            if (mounted.current) {
              setFailedIds((current) => (current.includes(id) ? current : [...current, id]));
            }
          });
      });
  }, [specialtyIdsKey]);

  const loadFailed = specialties.some((specialty) => failedIds.includes(specialty.id));
  const showSpecialtyName = specialties.length > 1;
  const labelFor = (aoiName: string, aoiSpecialtyId: number) => {
    const specialtyName = specialties.find((specialty) => specialty.id === aoiSpecialtyId)?.name;
    return showSpecialtyName && specialtyName ? `${aoiName} (${specialtyName})` : aoiName;
  };
  const options: SubspecialtyOption[] = specialties.flatMap((specialty) =>
    (areasBySpecialty[specialty.id] ?? []).map((area) => ({
      label: labelFor(area.aoiName, specialty.id),
      subspecialty: {
        aoiName: area.aoiName,
        aoiSpecialtyId: specialty.id,
        areaOfInterestId: area.id,
      },
      value: String(area.id),
    }))
  );
  const selectedOptions: SubspecialtyOption[] = formState.subspecialties.map((subspecialty) => ({
    label: labelFor(subspecialty.aoiName, subspecialty.aoiSpecialtyId),
    subspecialty,
    value: String(subspecialty.areaOfInterestId),
  }));

  return (
    <div className="mt-4" data-cy="provider-service-subspecialties">
      <FieldLabel htmlFor="service-listing-subspecialties">Subspecialties</FieldLabel>
      <div className="mt-2">
        <ReactSelect<SubspecialtyOption, true>
          aria-label="Subspecialties"
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
          components={{ Input: FormSelectInput }}
          inputId="service-listing-subspecialties"
          isClearable
          isDisabled={specialties.length === 0}
          isMulti
          menuPortalTarget={selectPortalProps.menuPortalTarget}
          menuPosition={selectPortalProps.menuPosition}
          noOptionsMessage={() => 'No subspecialties available'}
          onChange={(next: MultiValue<SubspecialtyOption>) =>
            setFormState((current) => {
              const subspecialties = next.map((option) => option.subspecialty);
              return {
                ...current,
                // The checkbox only shows with a subspecialty picked; none picked means general.
                generalSpecialtyFlag: subspecialties.length ? current.generalSpecialtyFlag : true,
                subspecialties,
              };
            })
          }
          options={options}
          placeholder={
            specialties.length === 0 ? 'Select a specialty first' : 'Select subspecialties...'
          }
          styles={{
            menuPortal: selectPortalProps.menuPortalStyle,
          }}
          unstyled
          value={selectedOptions}
          classNames={{
            ...SelectStyles(),
            multiValue: () => 'mx-1 rounded-md border border-primary-700 bg-white px-2',
            multiValueLabel: () => 'px-1 py-1 body-text',
            multiValueRemove: () =>
              'cursor-pointer rounded-r-md px-1 hover:bg-grey-900 hover:text-error-50',
          }}
        />
      </div>
      {loadFailed ? (
        <p className="mt-2 text-left text-xs text-danger" role="alert">
          Some subspecialties could not be loaded. Change the specialties to try again.
        </p>
      ) : null}
      {formState.subspecialties.length > 0 ? (
        <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-content-primary">
          <input
            checked={formState.generalSpecialtyFlag}
            className="mt-0.5 shrink-0"
            data-cy="provider-service-general-specialty"
            onChange={(event) =>
              setFormState((current) => ({
                ...current,
                generalSpecialtyFlag: event.target.checked,
              }))
            }
            type="checkbox"
          />
          <span>Open to general specialty work</span>
        </label>
      ) : null}
    </div>
  );
};

const SectionHeader = ({
  children,
  icon: Icon,
  title,
}: {
  children?: ReactNode;
  icon: typeof BriefcaseIcon;
  title: string;
}) => (
  <div className="flex items-start gap-3 text-left">
    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50">
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <h2 className="text-[1rem] font-semibold text-content-primary">{title}</h2>
      {children ? <p className="mt-1 text-sm text-content-secondary">{children}</p> : null}
    </div>
  </div>
);

const ListingTable = ({
  listings,
  onNew,
  selectedListingId,
  setSelectedListingId,
}: {
  listings: ServiceListing[];
  onNew: () => void;
  selectedListingId: number | null;
  setSelectedListingId: (id: number) => void;
}) => (
  <section className="rounded-lg border border-grey-900 bg-white text-left shadow-sm">
    <div className="border-b border-grey-900 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-content-primary">My Services</h1>
          <p className="mt-1 text-sm text-content-secondary">
            Provider-owned listings for employer inquiry and negotiation.
          </p>
        </div>
        <Button
          className="flex shrink-0 items-center gap-2"
          colorVariant="primary"
          cyTag="provider-service-new"
          label="New"
          onClick={onNew}
          size="sm"
          type="button"
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>

    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-grey-900 text-left">
        <thead className="bg-grey-925">
          <tr>
            <th className="px-5 py-3 text-xs font-medium uppercase tracking-wide text-content-tertiary">
              Listing
            </th>
            <th className="px-5 py-3 text-xs font-medium uppercase tracking-wide text-content-tertiary">
              Mode
            </th>
            <th className="px-5 py-3 text-xs font-medium uppercase tracking-wide text-content-tertiary">
              Next Availability
            </th>
            <th className="px-5 py-3 text-xs font-medium uppercase tracking-wide text-content-tertiary">
              Inquiries
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-grey-900 bg-white">
          {listings.length === 0 ? (
            <tr>
              <td className="px-5 py-8 text-sm text-content-secondary" colSpan={4}>
                No service listings yet.
              </td>
            </tr>
          ) : null}
          {listings.map((listing) => {
            const isSelected = listing.id === selectedListingId;
            const primaryTextClass = isSelected ? 'text-white' : 'text-content-primary';
            const secondaryTextClass = isSelected ? 'text-white' : 'text-content-secondary';
            const pillClass = isSelected ? 'bg-white text-primary-50' : 'bg-primary-50 text-white';

            return (
              <tr
                className={`cursor-pointer transition ${
                  isSelected ? 'bg-primary-50' : 'hover:bg-grey-925'
                }`}
                key={listing.id}
                onClick={() => setSelectedListingId(listing.id)}
              >
                <td className="px-5 py-4">
                  <button
                    aria-pressed={isSelected}
                    className="w-full rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedListingId(listing.id);
                    }}
                    type="button"
                  >
                    <span className={`block font-semibold ${primaryTextClass}`}>
                      {listing.title}
                    </span>
                    <span className={`mt-1 block text-sm ${secondaryTextClass}`}>
                      {listing.aoiSpecialties.join(', ')}
                    </span>
                    {listing.subspecialties.length > 0 ? (
                      <span className={`mt-1 block text-sm ${secondaryTextClass}`}>
                        {listing.subspecialties.join(', ')}
                      </span>
                    ) : null}
                    <span className="mt-2 flex flex-wrap gap-2">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${pillClass}`}>
                        {listing.status}
                      </span>
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${pillClass}`}>
                        {formatWorkLocation(listing.workLocation)}
                      </span>
                      {listing.subspecialties.length > 0 && !listing.generalSpecialtyFlag ? (
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${pillClass}`}>
                          Subspecialty work only
                        </span>
                      ) : null}
                    </span>
                  </button>
                </td>
                <td className={`px-5 py-4 text-sm ${primaryTextClass}`}>
                  {formatKind(listing.kind)}
                </td>
                <td className="px-5 py-4">
                  <p className={`text-sm ${primaryTextClass}`}>{listing.nextAvailability}</p>
                  <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                    {listing.availabilitySummary}
                  </p>
                </td>
                <td className={`px-5 py-4 text-sm ${primaryTextClass}`}>{listing.inquiryCount}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </section>
);

const ListingPreview = ({
  isArchiving,
  isLoadingEditor,
  isPublishing,
  listing,
  publicationFeedback,
  onEdit,
  onPublish,
  onShowArchive,
}: {
  isArchiving: boolean;
  isLoadingEditor: boolean;
  isPublishing: boolean;
  listing: ServiceListing;
  publicationFeedback?: PublicationFeedback | null;
  onEdit: () => void;
  onPublish: () => void;
  onShowArchive: () => void;
}) => {
  const feedback = publicationFeedback?.listingId === listing.id ? publicationFeedback : null;
  const feedbackRef = useRef<HTMLDivElement>(null);
  const publicationControlsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!feedback) return;
    if (feedback.outcome === 'error') feedbackRef.current?.focus({ preventScroll: true });
    // Keep the response and the recovery controls together, even below a long table.
    publicationControlsRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [feedback]);

  return (
    <section className="rounded-lg border border-grey-900 bg-white p-5 text-left shadow-sm">
      <div className="flex flex-col gap-4 border-b border-grey-900 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
            Search Card Preview
          </p>
          <h2 className="mt-2 text-xl font-semibold text-content-primary">{listing.title}</h2>
          <p className="mt-1 text-sm text-content-secondary">
            Masked provider profile until the existing HealthcareLX unmask flow allows disclosure.
          </p>
        </div>
        <div
          className="flex w-full shrink-0 flex-col items-start gap-3 sm:w-80 sm:items-end"
          ref={publicationControlsRef}
        >
          <span className="rounded-full bg-success-subtle px-3 py-1 text-xs font-semibold text-good">
            {listing.status}
          </span>
          <div className="flex flex-wrap gap-2">
            {listing.status === 'DRAFT' || listing.status === 'PAUSED' ? (
              <Button
                className="inline-flex items-center justify-center gap-2"
                cyTag="provider-service-publish"
                disabled={isPublishing || isLoadingEditor || isArchiving}
                label={isPublishing ? 'Publishing...' : 'Publish'}
                onClick={onPublish}
                size="sm"
                type="button"
              >
                <CheckCircleIcon aria-hidden="true" className="h-4 w-4" />
              </Button>
            ) : null}
            <Button
              className="inline-flex items-center justify-center gap-2"
              colorVariant="outline"
              disabled={isPublishing || isLoadingEditor || isArchiving}
              label={isLoadingEditor ? 'Loading...' : 'Edit'}
              onClick={onEdit}
              size="sm"
              type="button"
            >
              <PencilSquareIcon aria-hidden="true" className="h-4 w-4" />
            </Button>
            <Button
              className="inline-flex items-center justify-center gap-2"
              colorVariant="error"
              disabled={isPublishing || isLoadingEditor || isArchiving}
              label="Archive"
              onClick={onShowArchive}
              size="sm"
              type="button"
            >
              <ArchiveBoxIcon aria-hidden="true" className="h-4 w-4" />
            </Button>
          </div>
          {feedback ? (
            <div
              className={`w-full rounded-md border px-3 py-3 text-left text-sm ${
                feedback.outcome === 'error'
                  ? 'border-danger/20 bg-danger-subtle text-danger'
                  : 'border-success/20 bg-success-subtle text-good'
              }`}
              data-cy="provider-service-publication-feedback"
              ref={feedbackRef}
              role={feedback.outcome === 'error' ? 'alert' : 'status'}
              tabIndex={-1}
            >
              {feedback.message}
            </div>
          ) : !isPublishing &&
            (listing.status === 'DRAFT' || listing.status === 'PAUSED') &&
            listing.publishRequirements?.length ? (
            <p
              className="w-full text-left text-sm text-content-secondary"
              data-cy="provider-service-publish-requirements"
            >
              {listing.publishRequirements.join(' ')}
            </p>
          ) : null}
        </div>
      </div>

      <dl className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
            Mode
          </dt>
          <dd className="mt-1 text-sm text-content-primary">{formatKind(listing.kind)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
            Expected Rates
          </dt>
          <dd className="mt-1 text-sm text-content-primary">{listing.compSummary}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
            License States
          </dt>
          <dd className="mt-1 text-sm text-content-primary">{listing.licenseStates.join(', ')}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
            Geography
          </dt>
          <dd className="mt-1 text-sm text-content-primary">{listing.geography}</dd>
        </div>
      </dl>

      <div className="mt-5 rounded-lg bg-grey-925 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
          Interaction
        </p>
        <p className="mt-1 text-sm text-content-primary">
          Employer inquiry, structured counter terms, and negotiation.
        </p>
      </div>
    </section>
  );
};

const BasicsSection = ({
  formState,
  heldLicenseStates,
  licenseStateOptions,
  radiusError,
  setFormState,
  specialtyOptions,
  specialtyOptionsError,
}: {
  formState: FormState;
  /** The license states the saved listing already holds, whether or not they are still current. */
  heldLicenseStates: string[];
  licenseStateOptions: string[];
  radiusError?: string;
  setFormState: Dispatch<SetStateAction<FormState>>;
  specialtyOptions: string[];
  specialtyOptionsError: string | null;
}) => {
  const showGeography = formState.workLocation !== 'REMOTE';
  const visibleSpecialtyOptions = [...new Set([...specialtyOptions, ...formState.aoiSpecialties])];
  // [MCV-1864] The current licenses, plus any state the saved listing already holds: that one
  // stays shown (and selected) but can only be turned off, never turned back on.
  const shownLicenseStates = [
    ...new Set([
      ...licenseStateOptions,
      ...heldLicenseStates.filter((state) => !licenseStateOptions.includes(state)),
      ...formState.licenseStates.filter((state) => !licenseStateOptions.includes(state)),
    ]),
  ];

  return (
    <section className="rounded-lg border border-grey-900 bg-white p-5 text-left shadow-sm">
      <SectionHeader icon={BriefcaseIcon} title="Basics">
        Single-mode provider service listing details.
      </SectionHeader>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <FieldLabel htmlFor="service-listing-title">Title</FieldLabel>
          <TextInput
            id="service-listing-title"
            onChange={(title) => setFormState((current) => ({ ...current, title }))}
            value={formState.title}
          />
        </div>

        <div className="lg:col-span-2">
          <FieldLabel>Mode</FieldLabel>
          <div className="mt-2 grid gap-3 md:grid-cols-3">
            {LISTING_KIND_OPTIONS.map((option) => {
              const isSelected = option.value === formState.kind;

              return (
                <button
                  aria-pressed={isSelected}
                  className={`rounded-lg border p-4 text-left transition ${
                    isSelected
                      ? 'border-primary-50 bg-primary-50 text-white'
                      : 'border-grey-800 bg-white hover:bg-grey-925'
                  }`}
                  key={option.value}
                  onClick={() => setFormState((current) => ({ ...current, kind: option.value }))}
                  type="button"
                >
                  <p
                    className={`font-semibold ${isSelected ? 'text-white' : 'text-content-primary'}`}
                  >
                    {option.label}
                  </p>
                  <p
                    className={`mt-1 text-sm ${
                      isSelected ? 'text-white' : 'text-content-secondary'
                    }`}
                  >
                    {option.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <FieldLabel htmlFor="service-listing-aoi-specialties">AOI Specialties</FieldLabel>
          <AoiSpecialtiesSelect
            onChange={(aoiSpecialties) =>
              setFormState((current) =>
                withoutOrphanedSubspecialties({ ...current, aoiSpecialties })
              )
            }
            options={visibleSpecialtyOptions}
            selected={formState.aoiSpecialties}
          />
          {specialtyOptionsError ? (
            <p className="mt-2 text-left text-xs text-danger" role="alert">
              {specialtyOptionsError}
            </p>
          ) : null}
          <SubspecialtiesSelect formState={formState} setFormState={setFormState} />
        </div>

        <div>
          <FieldLabel htmlFor="custom-specialty">Additional AOI Specialty</FieldLabel>
          <TextInput
            id="custom-specialty"
            onChange={(customSpecialty) =>
              setFormState((current) =>
                withoutOrphanedSubspecialties({ ...current, customSpecialty })
              )
            }
            placeholder="Type specialty"
            value={formState.customSpecialty}
          />
          <p className="mt-1 text-left text-xs text-content-tertiary">
            The name must match an available canonical specialty.
          </p>
        </div>

        <div>
          <FieldLabel>Work Location</FieldLabel>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {WORK_LOCATION_OPTIONS.map((option) => (
              <button
                aria-pressed={formState.workLocation === option.value}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition ${
                  formState.workLocation === option.value
                    ? 'border-primary-50 bg-primary-50 text-white'
                    : 'border-grey-800 bg-white text-content-secondary hover:bg-grey-925'
                }`}
                key={option.value}
                onClick={() =>
                  setFormState((current) => ({ ...current, workLocation: option.value }))
                }
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <FieldLabel>License States</FieldLabel>
          {licenseStateOptions.length === 0 ? (
            <p className="mt-2 text-left text-sm text-content-secondary">
              Add an active medical license in your vault to choose license states.
            </p>
          ) : null}
          <MultiSelectPills
            isOptionDisabled={(state) => !licenseStateOptions.includes(state)}
            onToggle={(state) =>
              setFormState((current) => ({
                ...current,
                licenseStates: toggleValue(current.licenseStates, state),
              }))
            }
            options={shownLicenseStates}
            selected={formState.licenseStates}
          />
        </div>

        {showGeography ? (
          <div className="lg:col-span-2">
            <FieldLabel>Geographic Preferences</FieldLabel>
            <div className="mt-2 grid gap-3 md:grid-cols-[minmax(0,1fr)_140px]">
              <div>
                <FieldLabel htmlFor="geography-query">Location</FieldLabel>
                <LocationSelect formState={formState} setFormState={setFormState} />
              </div>
              <div>
                <FieldLabel htmlFor="radius-miles">Radius (miles)</FieldLabel>
                <TextInput
                  error={radiusError}
                  id="radius-miles"
                  onChange={(radiusMiles) =>
                    setFormState((current) => ({ ...current, radiusMiles }))
                  }
                  type="number"
                  value={formState.radiusMiles}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-grey-925 p-4 lg:col-span-2">
            <p className="text-sm font-medium text-content-primary">Remote listing</p>
            <p className="mt-1 text-sm text-content-secondary">
              Distance preferences are skipped; license-state filters remain available.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

const RatesSection = ({
  errors,
  formState,
  setFormState,
}: {
  errors: ExpectedRateErrors;
  formState: FormState;
  setFormState: Dispatch<SetStateAction<FormState>>;
}) => (
  <section className="rounded-lg border border-grey-900 bg-white p-5 text-left shadow-sm">
    <SectionHeader icon={CheckCircleIcon} title="Expected Rates">
      Negotiable expectations using the existing job listing compensation anchors.
    </SectionHeader>

    <div className="mt-5 grid gap-4 md:grid-cols-2">
      <div>
        <FieldLabel htmlFor="comp-type">Compensation Anchor</FieldLabel>
        <select
          className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-sm text-content-primary outline-none transition focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12"
          id="comp-type"
          onChange={(event) =>
            setFormState((current) => ({ ...current, compType: event.target.value as CompType }))
          }
          value={formState.compType}
        >
          <option value="HOUR">Hourly</option>
          <option value="SHIFT">Per shift</option>
          <option value="RVU">Per RVU</option>
        </select>
      </div>
      <div>
        <FieldLabel htmlFor="hours-per-shift">Hours Per Shift</FieldLabel>
        <TextInput
          error={errors.hoursPerShift}
          id="hours-per-shift"
          min={0}
          onChange={(hoursPerShift) => setFormState((current) => ({ ...current, hoursPerShift }))}
          step={0.1}
          type="number"
          value={formState.hoursPerShift}
        />
      </div>
      {formState.compType === 'HOUR' ? (
        <div>
          <FieldLabel htmlFor="price-per-hour">Expected Hourly Rate</FieldLabel>
          <TextInput
            error={errors.pricePerHour}
            id="price-per-hour"
            min={0}
            onChange={(pricePerHour) => setFormState((current) => ({ ...current, pricePerHour }))}
            step={0.01}
            type="number"
            value={formState.pricePerHour}
          />
        </div>
      ) : null}
      {formState.compType === 'SHIFT' ? (
        <div>
          <FieldLabel htmlFor="price-per-shift">Expected Shift Rate</FieldLabel>
          <TextInput
            error={errors.pricePerShift}
            id="price-per-shift"
            min={0}
            onChange={(pricePerShift) => setFormState((current) => ({ ...current, pricePerShift }))}
            step={0.01}
            type="number"
            value={formState.pricePerShift}
          />
        </div>
      ) : null}
      {formState.compType === 'RVU' ? (
        <div>
          <FieldLabel htmlFor="price-per-rvu">Expected RVU Rate</FieldLabel>
          <TextInput
            error={errors.pricePerRvu}
            id="price-per-rvu"
            min={0}
            onChange={(pricePerRvu) => setFormState((current) => ({ ...current, pricePerRvu }))}
            step={0.01}
            type="number"
            value={formState.pricePerRvu}
          />
        </div>
      ) : null}
      <div>
        <FieldLabel htmlFor="call-rate">Expected Call Rate</FieldLabel>
        <TextInput
          error={errors.pricePerCallShift}
          id="call-rate"
          min={0}
          onChange={(pricePerCallShift) =>
            setFormState((current) => ({ ...current, pricePerCallShift }))
          }
          step={0.01}
          type="number"
          value={formState.pricePerCallShift}
        />
      </div>
    </div>
  </section>
);

const WorkPreferencesSection = ({
  formState,
  setFormState,
}: {
  formState: FormState;
  setFormState: Dispatch<SetStateAction<FormState>>;
}) => (
  <section className="rounded-lg border border-grey-900 bg-white p-5 text-left shadow-sm">
    <SectionHeader icon={ClockIcon} title="Work Preferences">
      Listing-level preferences stay here. Specific dates, shifts, and recurring rules are managed
      in Calendar.
    </SectionHeader>

    <div className="mt-5 grid gap-4 md:grid-cols-2">
      <div>
        <FieldLabel htmlFor="preferred-start-date">Preferred Work Start Date</FieldLabel>
        <TextInput
          id="preferred-start-date"
          onChange={(preferredStartDate) =>
            setFormState((current) => ({ ...current, preferredStartDate }))
          }
          type="date"
          value={formState.preferredStartDate}
        />
      </div>

      {formState.kind === 'PART_TIME' ? (
        <>
          <div>
            <FieldLabel htmlFor="max-shifts-week">Max Shifts Per Week</FieldLabel>
            <TextInput
              id="max-shifts-week"
              onChange={(maxShiftsPerWeek) =>
                setFormState((current) => ({ ...current, maxShiftsPerWeek }))
              }
              step={1}
              type="number"
              value={formState.maxShiftsPerWeek}
            />
          </div>
          <div>
            <FieldLabel htmlFor="max-shifts-month">Max Shifts Per Month</FieldLabel>
            <TextInput
              id="max-shifts-month"
              onChange={(maxShiftsPerMonth) =>
                setFormState((current) => ({ ...current, maxShiftsPerMonth }))
              }
              step={1}
              type="number"
              value={formState.maxShiftsPerMonth}
            />
          </div>
        </>
      ) : null}

      {formState.kind !== 'LOCUMS' ? (
        <>
          <div>
            <FieldLabel htmlFor="call-preference">Call Preference</FieldLabel>
            <select
              className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-sm text-content-primary outline-none transition focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12"
              id="call-preference"
              onChange={(event) =>
                setFormState((current) => ({
                  ...current,
                  callPreference: event.target.value as FormState['callPreference'],
                }))
              }
              value={formState.callPreference}
            >
              <option value="NONE">No call</option>
              <option value="LIMITED">Limited call</option>
              <option value="REGULAR">Regular call</option>
              <option value="ANY">Open to call</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <FieldLabel>Preferred Shifts</FieldLabel>
            <MultiSelectPills
              onToggle={(shiftLabel) =>
                setFormState((current) => ({
                  ...current,
                  shiftLabels: toggleValue(current.shiftLabels, shiftLabel),
                }))
              }
              options={SHIFT_LABEL_OPTIONS}
              selected={formState.shiftLabels}
            />
          </div>
        </>
      ) : (
        <div className="rounded-lg bg-grey-925 p-4">
          <p className="text-sm font-medium text-content-primary">Locums shift details</p>
          <p className="mt-1 text-sm text-content-secondary">
            Add active and call shift windows to Calendar after saving this listing.
          </p>
        </div>
      )}
    </div>

    <div className="mt-4">
      <FieldLabel htmlFor="schedule-notes">Schedule Notes</FieldLabel>
      <textarea
        className="mt-1 min-h-[88px] w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-sm text-content-primary outline-none transition focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12"
        id="schedule-notes"
        onChange={(event) =>
          setFormState((current) => ({ ...current, scheduleNotes: event.target.value }))
        }
        value={formState.scheduleNotes}
      />
    </div>
  </section>
);

/** Show publish requirements and offer calendar setup after saving non-full-time listings. */
const VisibilitySection = ({
  availabilityReady,
  calendarEnabled,
  formState,
  isSaving,
  onSaveAndOpenCalendar,
}: {
  availabilityReady: boolean;
  calendarEnabled: boolean;
  formState: FormState;
  isSaving: boolean;
  onSaveAndOpenCalendar: () => void;
}) => {
  const validationRows = [
    {
      label: 'AOI specialty selected',
      valid: formState.aoiSpecialties.length > 0 || Boolean(formState.customSpecialty.trim()),
    },
    { label: 'License state selected', valid: formState.licenseStates.length > 0 },
    {
      label: 'Rate anchor entered',
      valid:
        (formState.compType === 'HOUR' &&
          parseNonnegativeNumber(formState.pricePerHour) !== undefined) ||
        (formState.compType === 'SHIFT' &&
          parseNonnegativeNumber(formState.pricePerShift) !== undefined) ||
        (formState.compType === 'RVU' &&
          parseNonnegativeNumber(formState.pricePerRvu) !== undefined),
    },
    {
      label: 'Geography ready for onsite/hybrid',
      valid: formState.workLocation === 'REMOTE' || Boolean(formState.geographyQuery.trim()),
    },
    {
      label:
        formState.kind === 'FULL_TIME'
          ? 'Preferred start date configured'
          : 'Availability configured in Calendar',
      valid: availabilityReady,
    },
  ];

  return (
    <section className="rounded-lg border border-grey-900 bg-white p-5 text-left shadow-sm">
      <SectionHeader icon={CheckCircleIcon} title="Visibility">
        Public employer discovery with HealthcareLX masking until unmask.
      </SectionHeader>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {validationRows.map((row) => (
          <div className="flex items-center gap-2 rounded-md bg-grey-925 px-3 py-2" key={row.label}>
            <CheckCircleIcon
              className={`h-5 w-5 ${row.valid ? 'text-good' : 'text-content-tertiary'}`}
            />
            <span className="text-sm text-content-primary">{row.label}</span>
          </div>
        ))}
      </div>
      {formState.kind !== 'FULL_TIME' && calendarEnabled ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-content-secondary">
            {formState.kind === 'PART_TIME'
              ? 'Add recurring availability in Calendar before publishing. Save your changes to continue.'
              : 'Add future available dates in Calendar before publishing. Save your changes to continue.'}
          </p>
          <Button
            colorVariant="outline"
            cyTag="provider-service-save-open-calendar"
            disabled={isSaving}
            label={isSaving ? 'Saving...' : 'Save and Open Calendar'}
            onClick={onSaveAndOpenCalendar}
            type="button"
          />
        </div>
      ) : null}
    </section>
  );
};

const FormPreview = ({ formState }: { formState: FormState }) => {
  const primaryRateValue =
    formState.compType === 'HOUR'
      ? formState.pricePerHour
      : formState.compType === 'SHIFT'
        ? formState.pricePerShift
        : formState.pricePerRvu;
  const parsedPrimaryRate = parseNonnegativeNumber(primaryRateValue);
  const rateSuffix =
    formState.compType === 'HOUR' ? 'hr' : formState.compType === 'SHIFT' ? 'shift' : 'RVU';
  const primaryRate =
    parsedPrimaryRate === undefined ? 'Rate not set' : `$${parsedPrimaryRate}/${rateSuffix}`;

  const geography =
    formState.workLocation === 'REMOTE'
      ? 'Remote'
      : `${formState.geographyQuery.trim() || 'Location not set'} within ${
          formState.radiusMiles || '0'
        } mi`;

  return (
    <aside className="rounded-lg border border-grey-900 bg-white p-5 text-left shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-content-tertiary">Draft</p>
      <h2 className="mt-2 text-lg font-semibold text-content-primary">{formState.title}</h2>
      <div className="mt-4 space-y-3 text-sm">
        {/* [MCV-1853] Top-aligned icons and wrapping text: a long location must not push the icon. */}
        <div className="flex items-start gap-2 text-content-secondary">
          <BriefcaseIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">{formatKind(formState.kind)}</span>
        </div>
        <div className="flex items-start gap-2 text-content-secondary">
          <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {formatWorkLocation(formState.workLocation)} - {geography}
          </span>
        </div>
        <div className="flex items-start gap-2 text-content-secondary">
          <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {parsedPrimaryRate === undefined ? primaryRate : `${primaryRate} expected, negotiable`}
          </span>
        </div>
      </div>
      <div className="mt-5 border-t border-grey-900 pt-4">
        <p className="text-sm font-medium text-content-primary">Specialties</p>
        <p className="mt-1 text-sm text-content-secondary">
          {[...formState.aoiSpecialties, formState.customSpecialty].filter(Boolean).join(', ') ||
            'None selected'}
        </p>
        {formState.subspecialties.length > 0 ? (
          <>
            <p className="mt-3 text-sm font-medium text-content-primary">Subspecialties</p>
            <p className="mt-1 text-sm text-content-secondary">
              {formState.subspecialties.map((subspecialty) => subspecialty.aoiName).join(', ')}
            </p>
            {!formState.generalSpecialtyFlag ? (
              <p className="mt-1 text-sm text-content-secondary">Subspecialty work only</p>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium text-content-primary">License States</p>
        <p className="mt-1 text-sm text-content-secondary">
          {formState.licenseStates.join(', ') || 'None selected'}
        </p>
      </div>
    </aside>
  );
};

/** Edit listing details with separate actions to save or continue into calendar setup. */
const ProviderServiceListingFormDialog = ({
  availabilityReady,
  calendarEnabled,
  editingListingId,
  expectedRateErrors,
  formError,
  formState,
  heldLicenseStates,
  isOpen,
  isSaving,
  licenseStateOptions,
  onClose,
  onFormStateChange,
  onSaveDraft,
  onSaveAndOpenCalendar,
  radiusError,
  specialtyOptions,
  specialtyOptionsError,
}: {
  availabilityReady: boolean;
  calendarEnabled: boolean;
  editingListingId: number | null;
  expectedRateErrors: ExpectedRateErrors;
  formError: string | null;
  formState: FormState;
  heldLicenseStates: string[];
  isOpen: boolean;
  isSaving: boolean;
  licenseStateOptions: string[];
  onClose: () => void;
  onFormStateChange: Dispatch<SetStateAction<FormState>>;
  onSaveDraft: () => void;
  onSaveAndOpenCalendar: () => void;
  radiusError: string | undefined;
  specialtyOptions: string[];
  specialtyOptionsError: string | null;
}) => (
  <FormDialog
    creating={isOpen && editingListingId == null}
    editing={isOpen && editingListingId != null}
    onClose={onClose}
    title={editingListingId == null ? 'New service listing' : 'Edit service listing'}
    width="5xl"
  >
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSaveDraft();
      }}
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(260px,0.8fr)]">
        <div className="space-y-4">
          <BasicsSection
            formState={formState}
            heldLicenseStates={heldLicenseStates}
            licenseStateOptions={licenseStateOptions}
            radiusError={radiusError}
            setFormState={onFormStateChange}
            specialtyOptions={specialtyOptions}
            specialtyOptionsError={specialtyOptionsError}
          />
          <RatesSection
            errors={expectedRateErrors}
            formState={formState}
            setFormState={onFormStateChange}
          />
          <WorkPreferencesSection formState={formState} setFormState={onFormStateChange} />
          <VisibilitySection
            availabilityReady={availabilityReady}
            calendarEnabled={calendarEnabled}
            formState={formState}
            isSaving={isSaving}
            onSaveAndOpenCalendar={onSaveAndOpenCalendar}
          />
        </div>
        <div className="xl:sticky xl:top-0 xl:self-start">
          <FormPreview formState={formState} />
        </div>
      </div>

      {formError ? (
        <div
          aria-live="assertive"
          className="mt-4 rounded-md border border-danger/20 bg-danger-subtle px-4 py-3 text-sm text-danger"
          role="alert"
        >
          {formError}
        </div>
      ) : null}

      <FormActions className="mt-5">
        <Button colorVariant="outline" label="Cancel" onClick={onClose} type="button" />
        <Button
          disabled={isSaving}
          label={isSaving ? 'Saving...' : editingListingId == null ? 'Save Draft' : 'Save Changes'}
          type="submit"
        />
      </FormActions>
    </form>
  </FormDialog>
);

const ArchiveConfirmationDialog = ({
  error,
  isArchiving,
  isOpen,
  listing,
  onArchive,
  onClose,
}: {
  error: string | null;
  isArchiving: boolean;
  isOpen: boolean;
  listing?: ServiceListing;
  onArchive: () => void;
  onClose: () => void;
}) => (
  <FormDialog creating={isOpen} onClose={onClose} title="Archive service listing" width="md">
    <p className="text-left text-sm text-content-secondary">
      Archive <strong className="text-content-primary">{listing?.title}</strong>? It will be removed
      from My Services and employer discovery, while existing response and negotiation history is
      preserved.
    </p>
    {error ? (
      <div
        aria-live="assertive"
        className="mt-4 rounded-md border border-danger/20 bg-danger-subtle px-4 py-3 text-sm text-danger"
        role="alert"
      >
        {error}
      </div>
    ) : null}
    <FormActions className="mt-5">
      <Button
        colorVariant="outline"
        disabled={isArchiving}
        label="Cancel"
        onClick={onClose}
        type="button"
      />
      <Button
        colorVariant="error"
        disabled={isArchiving}
        label={isArchiving ? 'Archiving...' : 'Archive Listing'}
        onClick={onArchive}
        type="button"
      />
    </FormActions>
  </FormDialog>
);

export type ProviderServiceListingsViewProps = {
  archiveError: string | null;
  availabilityMode: AvailabilityMode;
  availabilityReady: boolean;
  calendarDays: AvailabilityDay[];
  calendarEnabled: boolean;
  calendarError: string | null;
  editingListingId: number | null;
  expectedRateErrors: ExpectedRateErrors;
  formError: string | null;
  formState: FormState;
  /** License states the listing being edited already holds (none for a new listing). */
  heldLicenseStates?: string[];
  isArchiveDialogOpen: boolean;
  isArchiving: boolean;
  isCreateDialogOpen: boolean;
  isCalendarLoading: boolean;
  isLoadingEditor: boolean;
  isPublishing: boolean;
  isSaving: boolean;
  licenseStateOptions: string[];
  listings: ServiceListing[];
  onAvailabilityModeChange: (mode: AvailabilityMode) => void;
  onArchive: () => void;
  onCancelArchive: () => void;
  onCloseCreateDialog: () => void;
  onEdit: () => void;
  onFormStateChange: Dispatch<SetStateAction<FormState>>;
  onNew: () => void;
  onPublish: () => void;
  onSaveDraft: () => void;
  onSaveAndOpenCalendar: () => void;
  onSelectListing: (id: number) => void;
  onShowArchive: () => void;
  publicationFeedback?: PublicationFeedback | null;
  radiusError: string | undefined;
  recurringRules: AvailabilityRule[];
  selectedListing?: ServiceListing;
  selectedListingId: number | null;
  specialtyOptions: string[];
  specialtyOptionsError: string | null;
  statusMessage: string | null;
};

/** Present listing selection, availability readiness, and listing lifecycle dialogs. */
export const ProviderServiceListingsView = ({
  archiveError,
  availabilityMode,
  availabilityReady,
  calendarDays,
  calendarEnabled,
  calendarError,
  editingListingId,
  expectedRateErrors,
  formError,
  formState,
  heldLicenseStates = [],
  isArchiveDialogOpen,
  isArchiving,
  isCreateDialogOpen,
  isCalendarLoading,
  isLoadingEditor,
  isPublishing,
  isSaving,
  licenseStateOptions,
  listings,
  onAvailabilityModeChange,
  onArchive,
  onCancelArchive,
  onCloseCreateDialog,
  onEdit,
  onFormStateChange,
  onNew,
  onPublish,
  onSaveDraft,
  onSaveAndOpenCalendar,
  onSelectListing,
  onShowArchive,
  publicationFeedback,
  radiusError,
  recurringRules,
  selectedListing,
  selectedListingId,
  specialtyOptions,
  specialtyOptionsError,
  statusMessage,
}: ProviderServiceListingsViewProps) => (
  <div className="space-y-4">
    {statusMessage ? (
      <div
        aria-live="polite"
        className="rounded-md border border-grey-900 bg-white px-4 py-3 text-sm text-content-secondary"
        role="status"
      >
        {statusMessage}
      </div>
    ) : null}
    <ListingTable
      listings={listings}
      onNew={onNew}
      selectedListingId={selectedListingId}
      setSelectedListingId={onSelectListing}
    />

    {/* [MCV-1946] The preview column that sat beside this was removed; a lone grid child left the calendar at ~69% width */}
    <div className="space-y-4">
      {selectedListing ? (
        <ListingPreview
          isArchiving={isArchiving}
          isLoadingEditor={isLoadingEditor}
          isPublishing={isPublishing}
          listing={selectedListing}
          publicationFeedback={publicationFeedback}
          onEdit={onEdit}
          onPublish={onPublish}
          onShowArchive={onShowArchive}
        />
      ) : null}
      <AvailabilitySummarySection
        calendarDays={calendarDays}
        calendarEnabled={calendarEnabled}
        calendarError={calendarError}
        isCalendarLoading={isCalendarLoading}
        mode={availabilityMode}
        onModeChange={onAvailabilityModeChange}
        recurringRules={recurringRules}
        selectedListing={selectedListing}
      />
    </div>
    <ProviderServiceListingFormDialog
      availabilityReady={availabilityReady}
      calendarEnabled={calendarEnabled}
      editingListingId={editingListingId}
      expectedRateErrors={expectedRateErrors}
      formError={formError}
      formState={formState}
      heldLicenseStates={heldLicenseStates}
      isOpen={isCreateDialogOpen}
      isSaving={isSaving}
      licenseStateOptions={licenseStateOptions}
      onClose={onCloseCreateDialog}
      onFormStateChange={onFormStateChange}
      onSaveDraft={onSaveDraft}
      onSaveAndOpenCalendar={onSaveAndOpenCalendar}
      radiusError={radiusError}
      specialtyOptions={specialtyOptions}
      specialtyOptionsError={specialtyOptionsError}
    />
    <ArchiveConfirmationDialog
      error={archiveError}
      isArchiving={isArchiving}
      isOpen={isArchiveDialogOpen}
      listing={selectedListing}
      onArchive={onArchive}
      onClose={onCancelArchive}
    />
  </div>
);
